/**
 * Vendor self-serve applications. License and insurance are optional.
 * Lenders, title companies, inspectors, and warranty plans are not listed.
 * Box 3 is the vendor payment attestation. Web and the iOS build both read it.
 */

const VENDOR_APPLICATION_BOX_3_TITLE = 'No payment to Pacarion.';
const VENDOR_APPLICATION_BOX_3_BODY =
  "My business hasn't paid or offered, and won't pay or offer, any fee, commission, gift, or other thing of value to Pacarion for this listing, its placement, or any referral. Any referral or compensation arrangement my business has with an agent, organization, or other user is solely between us. It must be disclosed to that agent, and it must comply with applicable law, including RESPA, Chapter 475, Florida Statutes, and the agent's brokerage rules. Pacarion is not a party to it and doesn't collect, process, or track those payments. If my business is a lender, title or settlement agent, insurer, home inspector, appraiser, or home-warranty company, I state it won't pay or give anything of value to anyone for referrals through Pacarion.";
const VENDOR_APPLICATION_BOX_3 = `${VENDOR_APPLICATION_BOX_3_TITLE} ${VENDOR_APPLICATION_BOX_3_BODY}`;
const VENDOR_TEXT_VERSION = '2026-10-16.vendor-application.v1';

const { isSettlementServiceCategory } = require('./vendorCategories');
const { serviceAreaFromInput } = require('./serviceArea');

function cleanName(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function vendorBox1(businessName) {
  const name = cleanName(businessName) || 'this business';
  return `I'm authorized to act for ${name}. The information I've provided, including our business name, contact details, services, service area, licenses, and insurance, is accurate and complete. I'll keep it up to date, and I'll tell Pacarion right away if any license or insurance policy lapses, is suspended, or is revoked.`;
}

function vendorBox2() {
  return "My business holds every license, registration, permit, and insurance policy that federal, state, and local law requires for the services we offer, including Florida licenses where required (for example, contractor licenses under Chapter 489, Florida Statutes). I understand that Pacarion doesn't verify, vet, endorse, or guarantee vendors, and that my business is solely responsible for its work and its dealings with homeowners.";
}

function vendorBox4() {
  return "I agree to the Pacarion Terms of Service (including Section 10.3, Vendor Disclaimer, and Section 13.4, Vendor Submissions) and the Privacy Policy. To the fullest extent permitted by law, my business will defend, indemnify, and hold harmless Pacarion LLC from third-party claims arising from our listing, our services, work, acts, or omissions, or any discount we offer, except to the extent a claim results from Pacarion's own gross negligence, fraud, or intentional misconduct.";
}

function vendorBox5() {
  return "My business offers the discount described below to Pacarion homeowners. We'll honor it exactly as stated, including any conditions and the end date shown, and we'll update or remove it through Pacarion before changing or ending it. The discount is offered only by my business, not by Pacarion or the agent.";
}

function vendorBoxes(businessName) {
  return [
    { box: 1, required: true, title: 'Authority and accuracy.', text: vendorBox1(businessName) },
    { box: 2, required: true, title: 'Licensing and insurance are our responsibility.', text: vendorBox2() },
    {
      box: 3,
      required: true,
      title: VENDOR_APPLICATION_BOX_3_TITLE,
      text: VENDOR_APPLICATION_BOX_3_BODY,
      full: VENDOR_APPLICATION_BOX_3,
    },
    { box: 4, required: true, title: 'Terms and indemnity.', text: vendorBox4() },
    { box: 5, required: false, title: 'Homeowner-only discount.', text: vendorBox5() },
  ];
}

function checkedFlag(value) {
  return value === true;
}

function validateVendorConsents(body) {
  const source = body || {};
  const boxes = {
    1: checkedFlag(source.box1),
    2: checkedFlag(source.box2),
    3: checkedFlag(source.box3) || checkedFlag(source.attested),
    4: checkedFlag(source.box4),
    5: checkedFlag(source.box5),
  };
  const errors = {};
  [1, 2, 3, 4].forEach((box) => {
    if (!boxes[box]) errors[`box${box}`] = 'Required';
  });
  const description = String(source.discountDescription || source.discount || '').trim();
  const conditions = String(source.discountConditions || source.discount_conditions || '').trim();
  const endRaw = String(source.discountEndDate || source.discount_end_date || '').trim();
  if (boxes[5] && description.length < 2) errors.discountDescription = 'Describe the discount';
  let endDate = null;
  if (boxes[5] && endRaw) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(endRaw)) errors.discountEndDate = 'Use a real end date';
    else endDate = endRaw;
  }
  return {
    ok: Object.keys(errors).length === 0,
    errors,
    boxes,
    discount: boxes[5] ? {
      description,
      conditions,
      endDate,
      untilRemoved: !endDate,
    } : null,
  };
}

function vendorConsentLog({
  businessName,
  submitterName,
  boxes,
  discount,
  ip,
  now = new Date(),
  vendorId = null,
  applicationId = null,
}) {
  const acceptedAt = (now instanceof Date ? now : new Date(now)).toISOString();
  const name = cleanName(submitterName) || cleanName(businessName);
  return vendorBoxes(businessName).map((item) => {
    const checked = Boolean(boxes && boxes[item.box]);
    const row = {
      box: item.box,
      required: item.required,
      checked,
      title: item.title,
      text: item.box === 3 ? VENDOR_APPLICATION_BOX_3 : item.text,
      text_version: VENDOR_TEXT_VERSION,
      submitter_name: name,
      accepted_at: checked ? acceptedAt : null,
      ip: ip ? String(ip).slice(0, 80) : null,
      vendor_id: vendorId || null,
      vendor_application_id: applicationId || null,
    };
    if (item.box === 5) {
      row.discount_description = discount ? discount.description : null;
      row.discount_conditions = discount ? discount.conditions : null;
      row.discount_end_date = discount ? discount.endDate : null;
      row.discount_until_removed = discount ? discount.untilRemoved === true : null;
    }
    return row;
  });
}

function publicVendorDiscount(discount) {
  if (!discount || !discount.description) return null;
  const lines = [discount.description];
  if (discount.conditions) lines.push(discount.conditions);
  lines.push(discount.endDate || 'until removed');
  return lines.join('\n');
}


const MAX_PHOTOS = 6;

function cleanList(value) {
  const raw = Array.isArray(value) ? value : String(value || '').split(/[,;\n]/);
  const seen = new Set();
  const items = [];
  raw.forEach((item) => {
    const text = String(item || '').trim().replace(/\s+/g, ' ');
    const key = text.toLowerCase();
    if (!text || seen.has(key)) return;
    seen.add(key);
    items.push(text);
  });
  return items;
}

const SETTLEMENT_FILL_IN = /\b(?:titles?|settlements?|escrows?|closings?|lenders?|mortgages?|apprais(?:al|als|er|ers)|inspect(?:or|ors|ion|ions)|insur(?:ance|ances|er|ers))\b/i;
const HOME_WARRANTY_FILL_IN = /\bhome\s+warrant(?:y|ies)\b/i;

function excludedTrade(trade) {
  const text = String(trade || '').trim();
  if (!text) return false;
  if (isSettlementServiceCategory(text)) return true;
  return SETTLEMENT_FILL_IN.test(text) || HOME_WARRANTY_FILL_IN.test(text);
}

function validZip(value) {
  return /^\d{5}$/.test(String(value || '').trim());
}

function validUrl(value) {
  try {
    const url = new URL(String(value || ''));
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch (error) {
    return false;
  }
}

function validEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
}

function phoneDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

/** An email or a phone is enough. A blank field is fine. A filled field must be usable. */
function readContact(body) {
  const source = body || {};
  const emailRaw = String(source.email || '').trim();
  const phoneRaw = String(source.phone || '').trim();
  const digits = phoneDigits(phoneRaw);
  const emailOk = emailRaw === '' || validEmail(emailRaw);
  const phoneOk = phoneRaw === '' || (digits.length >= 10 && digits.length <= 15);
  return {
    email: validEmail(emailRaw) ? emailRaw.toLowerCase() : '',
    phone: phoneOk && phoneRaw ? phoneRaw : '',
    emailOk,
    phoneOk,
    hasOne: validEmail(emailRaw) || (digits.length >= 10 && digits.length <= 15),
  };
}

function readStorm(body) {
  const source = body || {};
  const value = source.stormAvailable !== undefined ? source.stormAvailable : source.storm_available;
  if (value === true || value === 'yes') return true;
  if (value === false || value === 'no') return false;
  return null;
}

function validateVendorApplication(input) {
  const body = input || {};
  const errors = {};
  const businessName = String(body.businessName || body.business_name || '').trim();
  const trades = cleanList(body.trades);
  const area = serviceAreaFromInput(body);
  const zips = cleanList(body.zips || body.service_zips);
  const hours = String(body.hours || '').trim();
  // Photo links are no longer collected. An omitted field is an empty list.
  const photos = cleanList(body.photos || body.photo_urls);
  const licenseNumber = String(body.licenseNumber || body.license_number || '').trim();
  const insuranceNote = String(body.insuranceNote || body.insurance_note || '').trim();

  if (businessName.length < 2) errors.businessName = 'Enter the business name';
  if (trades.length === 0) errors.trades = 'Add at least one trade';
  const blocked = trades.filter(excludedTrade);
  if (blocked.length) errors.trades = `${blocked.join(', ')} cannot be listed`;
  if (area.error) errors.serviceArea = area.error;
  const badZips = zips.filter((zip) => !validZip(zip));
  if (badZips.length) errors.zips = 'ZIP codes are 5 digits';
  const badPhotos = photos.filter((url) => !validUrl(url));
  if (badPhotos.length) errors.photos = 'Photo links must be web addresses';
  if (photos.length > MAX_PHOTOS) errors.photos = `Add at most ${MAX_PHOTOS} photos`;
  const contact = readContact(body);
  if (!contact.hasOne) errors.contact = 'Add an email or a phone number';
  else {
    if (!contact.emailOk) errors.email = 'Enter a valid email';
    if (!contact.phoneOk) errors.phone = 'Enter a valid phone number';
  }
  const consents = validateVendorConsents(body);
  Object.assign(errors, consents.errors);
  const submitterName = String(body.submitterName || body.submitter_name || '').trim();

  const stormAvailable = readStorm(body);
  const discountOffer = consents.discount;
  return {
    ok: Object.keys(errors).length === 0,
    errors,
    data: {
      businessName,
      trades,
      counties: area.countyFips,
      serviceCountyFips: area.countyFips,
      serviceStatewide: area.statewide,
      zips,
      hours,
      stormAvailable,
      photos,
      discount: discountOffer ? publicVendorDiscount(discountOffer) : null,
      discountOffer,
      submitterName,
      consents,
      licenseNumber: licenseNumber || null,
      insuranceNote: insuranceNote || null,
      phone: contact.phone || null,
      email: contact.email || null,
      website: String(body.website || '').trim() || null,
      description: String(body.description || '').trim() || null,
    },
  };
}

/**
 * Standard categories plus an optional Other fill-in.
 * The fill-in is stored as its own trade. The word "Other" is not a category.
 */
function applicationTrades({ trades, otherChecked, otherText } = {}) {
  const selected = cleanList(trades).filter((name) => name.toLowerCase() !== 'other');
  if (!otherChecked) return { trades: selected, error: '' };
  const custom = cleanName(otherText);
  if (custom.length < 2) return { trades: selected, error: 'Name the other category.' };
  if (excludedTrade(custom)) return { trades: selected, error: `${custom} cannot be listed` };
  return { trades: cleanList(selected.concat(custom)), error: '' };
}

function vendorApplicationMissing(form, boxes) {
  const source = form || {};
  const checks = boxes && typeof boxes === 'object' ? boxes : {};
  const missing = [];
  if (!String(source.businessName || '').trim()) missing.push('Business name');
  const listed = applicationTrades({
    trades: source.trades,
    otherChecked: Array.isArray(source.trades) && source.trades.includes('Other'),
    otherText: source.otherTrade,
  });
  if (!Array.isArray(source.trades) || source.trades.length === 0 || listed.error) missing.push('A trade');
  if (serviceAreaFromInput(source).error) missing.push('State and counties');
  if (!readContact(source).hasOne) missing.push('An email or a phone number');
  const agreed = boxes === true || Boolean(checks[1] && checks[2] && checks[3] && checks[4]);
  if (!agreed) missing.push('The Vendor Terms');
  if (checks[5] && String(source.discount || '').trim().length < 2) missing.push('The discount description');
  return missing;
}

function vendorApplicationMissingSentence(missing) {
  const items = (Array.isArray(missing) ? missing : []).filter(Boolean);
  if (!items.length) return '';
  if (items.length === 1) return `Still needed: ${items[0]}.`;
  const last = items[items.length - 1];
  return `Still needed: ${items.slice(0, -1).join(', ')}, and ${last}.`;
}

function matchesServiceArea(vendor, place) {
  const zip = String((place && place.zip) || '').trim();
  const county = String((place && place.county) || '').trim().toLowerCase();
  const zips = vendor && (vendor.service_zips || vendor.zips || []);
  const counties = vendor && (vendor.service_counties || vendor.counties || []);
  if (zip && (zips || []).some((item) => String(item).trim() === zip)) return true;
  if (county && (counties || []).some((item) => String(item).trim().toLowerCase() === county)) return true;
  return false;
}

module.exports = {
  VENDOR_APPLICATION_BOX_3_TITLE,
  VENDOR_APPLICATION_BOX_3_BODY,
  VENDOR_APPLICATION_BOX_3,
  VENDOR_TEXT_VERSION,
  vendorBox1,
  vendorBox2,
  vendorBox4,
  vendorBox5,
  vendorBoxes,
  validateVendorConsents,
  vendorConsentLog,
  publicVendorDiscount,
  excludedTrade,
  applicationTrades,
  vendorApplicationMissing,
  vendorApplicationMissingSentence,
  validateVendorApplication,
  matchesServiceArea,
};
